"use client";

import React, { useState, useRef, useEffect, useCallback, useImperativeHandle } from "react";
import { Search, X, Loader2, Pencil } from "lucide-react";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";
import { useDropdownFloating } from "@/components/forms/useDropdownFloating";
import { FloatingPortal } from "@floating-ui/react";

export interface CreatableComboboxProps {
  label: string;
  placeholder?: string;
  value: { id: number; name: string } | null;
  onSelect: (item: { id: number; name: string } | null) => void | Promise<void>;
  searchResults: { id: number; name: string }[];
  isSearching?: boolean;
  onSearch: (query: string) => void;
  onCreate?: (name: string) => Promise<{ id: number; name: string }>;
  onDelete?: (id: number) => void;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  /** Compact mode: hides label, smaller padding/font for inline use */
  compact?: boolean;
  /**
   * When false, the X (clear) button is replaced by a pencil that enters a
   * local "replace" mode: the search input shows without ever emitting
   * ``onSelect(null)``, so the stored value is only changed by picking a new
   * item (Escape cancels back to the current value). Use for fields where
   * clearing is a privileged action but swapping is allowed.
   */
  allowClear?: boolean;
}

export interface CreatableComboboxHandle {
  commit: (options?: { allowFirstResult?: boolean }) => Promise<boolean>;
}

export const CreatableCombobox = React.forwardRef<CreatableComboboxHandle, CreatableComboboxProps>(function CreatableCombobox({
  label,
  placeholder = "Search or create...",
  value,
  onSelect,
  searchResults,
  isSearching = false,
  onSearch,
  onCreate,
  onDelete,
  disabled = false,
  error,
  required = false,
  compact = false,
  allowClear = true,
}: CreatableComboboxProps, ref) {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  // allowClear=false replace mode: search UI shown while the stored value
  // stays untouched until a new item is picked.
  const [isReplacing, setIsReplacing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { refs, floatingStyle, getFloatingProps } = useDropdownFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (!disabled) setIsOpen(open);
    },
    matchReferenceWidth: true,
  });

  const setInputRef = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    refs.setReference(node);
  }, [refs]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onSearch(inputValue);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, onSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleSelect = useCallback(
    async (item: { id: number; name: string }) => {
      setInputValue("");
      setIsOpen(false);
      setIsReplacing(false);
      await onSelect(item);
    },
    [onSelect]
  );

  const handleClear = useCallback(() => {
    void onSelect(null);
    setInputValue("");
    inputRef.current?.focus();
  }, [onSelect]);

  const startReplace = useCallback(() => {
    setIsReplacing(true);
    setInputValue("");
    // Focus after the search input mounts.
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const cancelReplace = useCallback(() => {
    setIsReplacing(false);
    setInputValue("");
    setIsOpen(false);
  }, []);

  const commit = useCallback(async (options?: { allowFirstResult?: boolean }) => {
    const nextValue = inputValue.trim();
    if (!nextValue || disabled) return true;
    if (isCreating) return false;

    const exactMatch = searchResults.find(
      (item) => item.name.toLowerCase() === nextValue.toLowerCase()
    );
    if (exactMatch) {
      await handleSelect(exactMatch);
      return true;
    }

    if (onCreate) {
      setIsCreating(true);
      try {
        const newItem = await onCreate(nextValue);
        await handleSelect(newItem);
        return true;
      } catch {
        // onCreate handles its own error display
        return false;
      } finally {
        setIsCreating(false);
      }
    }

    if (options?.allowFirstResult && searchResults.length > 0) {
      await handleSelect(searchResults[0]);
      return true;
    }

    return false;
  }, [disabled, handleSelect, inputValue, isCreating, onCreate, searchResults]);

  useImperativeHandle(ref, () => ({ commit }), [commit]);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      await commit({ allowFirstResult: true });
    } else if (e.key === "Escape") {
      if (isReplacing) {
        cancelReplace();
        return;
      }
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleDelete = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    onDelete?.(id);
  };

  const showDropdown =
    isOpen && !disabled && (searchResults.length > 0 || isSearching);

  return (
    <div className="relative w-full">
      {!compact && (
        <Typography as="label" variant="body" weight="medium" color="text-navigator-text-primary" className="block mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Typography>
      )}

      {value && !isReplacing ? (
        <div
          className={cn(
            "flex items-center justify-between w-full border",
            compact ? "px-2 py-1 rounded-lg text-xs" : "px-4 py-3 rounded-xl",
            error ? "border-red-300" : "border-gray-200",
            disabled ? "bg-gray-50 cursor-not-allowed" : "bg-gray-50"
          )}
        >
          <span className={cn(compact ? "text-xs font-medium text-gray-900" : "text-navigator-text-primary", "truncate")}>{value.name}</span>
          {!disabled && (
            <button
              type="button"
              aria-label={allowClear ? "Clear" : "Change"}
              title={allowClear ? "Clear" : "Change"}
              onClick={allowClear ? handleClear : startReplace}
              className="ml-2 p-0.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
            >
              {allowClear ? (
                <X className={compact ? "w-3 h-3" : "w-4 h-4"} />
              ) : (
                <Pencil className={compact ? "w-3 h-3" : "w-4 h-4"} />
              )}
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <div className={cn("absolute", compact ? "left-2" : "left-4", "top-1/2 transform -translate-y-1/2 text-gray-400")}>
            {isSearching || isCreating ? (
              <Loader2 className={cn(compact ? "w-3 h-3" : "w-4 h-4", "animate-spin")} />
            ) : (
              <Search className={compact ? "w-3 h-3" : "w-4 h-4"} />
            )}
          </div>
          <input
            ref={setInputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              "w-full border transition-all duration-200",
              compact ? "pl-7 pr-2 py-1 rounded-lg text-xs" : "pl-11 pr-4 py-3 rounded-xl",
              error
                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                : "border-gray-200 focus:border-navigator-primary focus:ring-navigator-primary/20",
              "focus:outline-none focus:ring-1",
              "bg-gray-50 text-gray-900 font-medium",
              "placeholder:text-gray-400",
              "disabled:bg-gray-50 disabled:cursor-not-allowed",
              isReplacing && (compact ? "pr-7" : "pr-10")
            )}
          />
          {isReplacing && (
            <button
              type="button"
              aria-label="Cancel change"
              title="Cancel change"
              onClick={cancelReplace}
              className={cn(
                "absolute top-1/2 -translate-y-1/2 p-0.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors",
                compact ? "right-2" : "right-3"
              )}
            >
              <X className={compact ? "w-3 h-3" : "w-4 h-4"} />
            </button>
          )}
        </div>
      )}

      {showDropdown && (
        <FloatingPortal>
        <div
          ref={refs.setFloating}
          style={floatingStyle}
          {...getFloatingProps()}
          className={cn("overflow-auto border border-gray-200 bg-white shadow-lg", compact ? "rounded-lg" : "rounded-xl")}
        >
          {isSearching ? (
            <div className={cn(compact ? "px-2 py-1.5 text-xs" : "px-4 py-3 text-sm", "text-gray-500")}>Searching...</div>
          ) : searchResults.length > 0 ? (
            searchResults.map((item) => (
              <div
                key={item.id}
                onClick={() => void handleSelect(item)}
                className={cn("group flex items-center justify-between hover:bg-gray-100 cursor-pointer", compact ? "px-2 py-1.5" : "px-4 py-2")}
              >
                <span className={cn(compact ? "text-xs" : "text-sm", "text-navigator-text-primary")}>
                  {item.name}
                </span>
                {onDelete && (
                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, item.id)}
                    className="p-0.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))
          ) : inputValue.trim().length > 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              {onCreate ? (
                <>Press <kbd className="px-1.5 py-0.5 rounded bg-gray-100 text-xs font-mono">Enter</kbd> to create &ldquo;{inputValue.trim()}&rdquo;</>
              ) : (
                "No results found"
              )}
            </div>
          ) : null}
        </div>
        </FloatingPortal>
      )}

      {error && <Typography as="p" variant="body" color="error" className="mt-1.5">{error}</Typography>}
    </div>
  );
});
